import { FC, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import { useAcceptInvitation } from "../../hooks/useAcceptInvitation";
import Button from "../../components/Button/Button";
import { ERoutes } from "../../constants/routes";
import { supabase } from "@/lib/supabase";
import InvitationRegisterForm from "./InvitationRegisterForm";

import styles from "./AcceptInvitation.module.css";

const AcceptInvitationView: FC = () => {
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { invitation, loading, error, accepting, accept, isAgencyInvite } = useAcceptInvitation(token);

    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

    useEffect(() => {
        supabase.auth.getSession().then(({ data }) => {
            setIsAuthenticated(!!data.session);
        });
    }, []);

    const handleAccept = async () => {
        const result = await accept();
        if (result && !("error" in result)) {
            navigate(ERoutes.Brands, { replace: true });
        }
    };

    // Still checking auth state or loading invitation data
    if (isAuthenticated === null || loading) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h1 className={styles.title}>Invitation</h1>
                    <p className={styles.loading}>Loading…</p>
                </div>
            </div>
        );
    }

    if (error && !invitation) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <h1 className={styles.title}>Invitation</h1>
                    <p className={styles.error}>{error}</p>
                </div>
            </div>
        );
    }

    if (!invitation) return null;

    // Unauthenticated user with a pending member invitation → show registration form
    // Agency invites don't require auth, so skip this check
    if (!isAgencyInvite && !isAuthenticated && invitation.status === "pending" && token) {
        return <InvitationRegisterForm token={token} invitation={invitation} />;
    }

    const isPending = invitation.status === "pending";

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <h1 className={styles.title}>Invitation</h1>
                <p className={styles.description}>
                    {isAgencyInvite ? (
                        <>
                            Agency <strong>{invitation.account_name ?? "an agency"}</strong> wants to manage your account.
                            {invitation.inviter_email && (
                                <> Invited by: <strong>{invitation.inviter_email}</strong>.</>                            )}
                        </>
                    ) : (
                        <>
                            You have been invited to join{" "}
                            <strong>{invitation.account_name ?? "an account"}</strong> as{" "}
                            {invitation.role}.
                            {invitation.inviter_email && (
                                <> Invited by: <strong>{invitation.inviter_email}</strong>.</>                            )}
                        </>
                    )}
                </p>
                {invitation.status !== "pending" && (
                    <p className={styles.status}>
                        This invitation is {invitation.status}.
                    </p>
                )}
                {isPending && (
                    <>
                        {error && <p className={styles.error}>{error}</p>}
                        <Button
                            type="button"
                            label={accepting ? "Accepting…" : "Accept invitation"}
                            typeStyle="fill-dark"
                            size="lg"
                            onClick={handleAccept}
                            disabled={accepting}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default AcceptInvitationView;
